import {
  KEY_PATTERN,
  MAX_LABEL_LENGTH,
  citedId,
  MAX_NAME_LENGTH,
  isStepType,
  isTaskType,
  quote,
  stepLabel,
  type MapModel,
} from './model.js';

/**
 * The checks a map must pass (ADR-017 §4, Decision 54.29). Every version is
 * checked by the same code, whatever made it: an AI draft, chat, the canvas,
 * the side panel or undo.
 */
export const MAP_CHECKS = [
  // The shape of the data.
  'invalid_step_type',
  'invalid_key',
  'invalid_name',
  'key_collision',
  'unknown_lane',
  'flow_to_missing_step',
  'self_flow',
  'duplicate_flow',
  'duplicate_citation',
  // The shape of the process.
  'no_start',
  'no_end',
  'start_incoming',
  'end_outgoing',
  'gateway_outgoing',
  'orphan_step',
  'unreachable_step',
] as const;
export type MapCheck = (typeof MAP_CHECKS)[number];

export interface MapIssue {
  check: MapCheck;
  /** The key of the lane, step or flow at fault; null when it is the whole map. */
  subject: string | null;
  message: string;
}

export type MapCheckResult =
  | { valid: true }
  | { valid: false; check: MapCheck; subject: string | null; message: string };

/**
 * Checks a whole map (E12-F09 §2): at least one start and one end; every step
 * reachable from a start; every decision with at least two outgoing flows; no
 * flow to a missing step; starts with no incoming flow and ends with no
 * outgoing flow; every step's lane exists; no step left unconnected. Keys,
 * types and names are checked first, so later checks can trust them.
 *
 * Returns the first problem in that order. Citation statuses are not checked
 * here: a step may keep citing an item withdrawn since (Decision 54.20). Only
 * new links must be to confirmed evidence, which `validateChangeSet` checks.
 */
export function validateMapModel(model: MapModel): MapCheckResult {
  const first = mapIssues(model)[0];
  return first ? { valid: false, ...first } : { valid: true };
}

/** Every problem with the map, in check order. Empty when the map is valid. */
export function mapIssues(model: MapModel): MapIssue[] {
  const issues: MapIssue[] = [];
  const add = (check: MapCheck, subject: string | null, message: string) =>
    issues.push({ check, subject, message });

  const laneKeys = new Set<string>();
  for (const lane of model.lanes) {
    if (!KEY_PATTERN.test(lane.key)) add('invalid_key', lane.key, badKey(lane.key));
    else if (laneKeys.has(lane.key))
      add('key_collision', lane.key, `Two lanes share the key ${quote(lane.key)}.`);
    laneKeys.add(lane.key);
    const why = textProblem(lane.name, MAX_NAME_LENGTH, true);
    if (why) add('invalid_name', lane.key, `The lane ${quote(lane.key)} ${why}.`);
  }

  const steps = new Map<string, MapModel['steps'][number]>();
  for (const step of model.steps) {
    if (!isStepType(step.type))
      add(
        'invalid_step_type',
        step.key,
        `${stepLabel(step)} has an unknown type ${quote(step.type)}.`,
      );
    if (!KEY_PATTERN.test(step.key)) add('invalid_key', step.key, badKey(step.key));
    else if (steps.has(step.key))
      add('key_collision', step.key, `Two steps share the key ${quote(step.key)}.`);
    steps.set(step.key, step);
    // A task names what is done; an event or a decision may be unnamed.
    const why = textProblem(step.name, MAX_NAME_LENGTH, isTaskType(step.type));
    if (why) add('invalid_name', step.key, `The step ${quote(step.key)} ${why}.`);
    const whySystem = step.system == null ? null : textProblem(step.system, MAX_NAME_LENGTH, true);
    if (whySystem) add('invalid_name', step.key, `The system of ${stepLabel(step)} ${whySystem}.`);
    if (!laneKeys.has(step.lane))
      add(
        'unknown_lane',
        step.key,
        `${stepLabel(step)} is in a lane that does not exist (${quote(step.lane)}).`,
      );
    const cited = new Set<string>();
    for (const c of step.citations) {
      const id = citedId(c);
      if (cited.has(`${c.kind}:${id}`))
        add('duplicate_citation', step.key, `${stepLabel(step)} cites the same item twice.`);
      cited.add(`${c.kind}:${id}`);
    }
  }

  const flowKeys = new Set<string>();
  const pairs = new Set<string>();
  const incoming = new Map<string, number>();
  const outgoing = new Map<string, string[]>();
  for (const flow of model.flows) {
    if (!KEY_PATTERN.test(flow.key)) add('invalid_key', flow.key, badKey(flow.key));
    else if (flowKeys.has(flow.key))
      add('key_collision', flow.key, `Two flows share the key ${quote(flow.key)}.`);
    flowKeys.add(flow.key);
    const why = flow.label == null ? null : textProblem(flow.label, MAX_LABEL_LENGTH, false);
    if (why) add('invalid_name', flow.key, `The label of the flow ${quote(flow.key)} ${why}.`);
    const from = steps.get(flow.from);
    const to = steps.get(flow.to);
    if (!from || !to) {
      const missing = from ? flow.to : flow.from;
      add(
        'flow_to_missing_step',
        flow.key,
        `A flow points to a step that is not on the map (${quote(missing)}).`,
      );
      continue;
    }
    if (from === to) {
      add('self_flow', flow.key, `A flow cannot lead from ${stepLabel(from)} back to itself.`);
      continue;
    }
    const pair = `${flow.from}\u0000${flow.to}`;
    if (pairs.has(pair))
      add(
        'duplicate_flow',
        flow.key,
        `There are two flows from ${stepLabel(from)} to ${stepLabel(to)}.`,
      );
    pairs.add(pair);
    incoming.set(flow.to, (incoming.get(flow.to) ?? 0) + 1);
    outgoing.set(flow.from, [...(outgoing.get(flow.from) ?? []), flow.to]);
  }

  const starts = model.steps.filter((s) => s.type === 'start_event');
  if (starts.length === 0) add('no_start', null, 'The map has no start.');
  if (!model.steps.some((s) => s.type === 'end_event')) add('no_end', null, 'The map has no end.');

  for (const step of model.steps) {
    const ins = incoming.get(step.key) ?? 0;
    const outs = outgoing.get(step.key)?.length ?? 0;
    if (step.type === 'start_event' && ins > 0)
      add(
        'start_incoming',
        step.key,
        `The start ${stepLabel(step)} has an incoming flow; a start has none.`,
      );
    if (step.type === 'end_event' && outs > 0)
      add(
        'end_outgoing',
        step.key,
        `The end ${stepLabel(step)} has an outgoing flow; an end has none.`,
      );
    if (step.type === 'exclusive_gateway' && outs < 2)
      add(
        'gateway_outgoing',
        step.key,
        `The decision ${stepLabel(step)} has ${outs === 0 ? 'no outgoing flows' : 'only one outgoing flow'}; a decision needs at least two.`,
      );
  }

  const orphans = new Set(
    model.steps.filter((s) => !incoming.has(s.key) && !outgoing.has(s.key)).map((s) => s.key),
  );
  for (const step of model.steps) {
    if (orphans.has(step.key))
      add('orphan_step', step.key, `${stepLabel(step)} is not connected to anything.`);
  }

  const reached = new Set<string>();
  const queue = starts.map((s) => s.key);
  while (queue.length > 0) {
    const key = queue.shift()!;
    if (reached.has(key)) continue;
    reached.add(key);
    queue.push(...(outgoing.get(key) ?? []));
  }
  for (const step of model.steps) {
    if (!reached.has(step.key) && !orphans.has(step.key))
      add('unreachable_step', step.key, `${stepLabel(step)} cannot be reached from a start.`);
  }

  return issues;
}

function badKey(key: unknown): string {
  return `${quote(String(key))} is not a valid key: use up to 64 letters, digits, “_” or “-”.`;
}

/** Why a name or label is not acceptable, or null. */
function textProblem(value: unknown, max: number, required: boolean): string | null {
  if (typeof value !== 'string') return 'has no name';
  const text = value.trim();
  if (required && text === '') return 'has no name';
  if (text.length > max) return `is longer than ${max} characters`;
  return null;
}
